import { Component, computed, input, output, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { SpinnerComponent } from '../shared/spinner.component';
import { ToastComponent } from '../shared/toast.component';
import { TitleCasePipe, UpperCasePipe } from '@angular/common';
import { UsersService } from './users.service';

@Component({
  selector: 'eaa-activate-deactive-user-dialog',
  imports: [DialogModule, ReactiveFormsModule, SpinnerComponent, TitleCasePipe, ToastComponent, UpperCasePipe],
  template: `
    <ejs-dialog
      #dgActivateDeactivate
      [showCloseIcon]="!processing"
      [isModal]="true"
      [visible]="false"
      width="800px"
      [closeOnEscape]="!processing"
    >
      <ng-template #content>
        <p>Are you sure you want to {{ label() }} the account of {{ userDisplayName() }} ({{ userEmail() }})?</p>
      </ng-template>
      <ng-template #header>{{ label() | titlecase }} {{ userDisplayName() }}</ng-template>
      <ng-template #footerTemplate>
        <div class="btn-group">
          <button class="btn btn-outline-secondary" (click)="close()" [disabled]="processing">CANCEL</button>
          <button class="btn btn-primary" (click)="activateOrDeactivateAccount()" [disabled]="processing">
            {{ label() | uppercase }}
          </button>
        </div>
      </ng-template>
    </ejs-dialog>

    <eaa-toast #toastReset2FaAction [showCloseButton]="false">
      <div id="title">PROCESSING</div>
      <div id="content" class="d-flex gap-2">
        <eaa-spinner />
        <div>
          {{ processingMessage }}
        </div>
      </div>
    </eaa-toast>
  `
})
export class ActivateDeactiveUserDialogComponent {
  protected processing = false;
  protected processingMessage = '';
  protected label = computed(() => (this.activate() ? 'activate' : 'deactivate'));

  public activate = input.required<boolean>();
  public userDisplayName = input.required<string | undefined>();
  public userEmail = input.required<string | undefined>();
  public userId = input.required<string | undefined>();
  public processComplete = output<void>();

  @ViewChild('dgActivateDeactivate')
  public dialog: DialogComponent | null = null;

  @ViewChild('toastReset2FaAction')
  public toastReset2FaAction?: ToastComponent;

  constructor(private usersService: UsersService) {}

  public close(): void {
    this.dialog?.hide();
  }

  public show(): void {
    // Reset state when showing dialog
    this.processing = false;
    this.processingMessage = '';
    this.dialog?.show();
  }

  public activateOrDeactivateAccount() {
    if (this.userId() === undefined) {
      return;
    }

    this.processing = true;
    this.processingMessage = this.activate()
      ? `Activating account of ${this.userDisplayName()}...`
      : `Deactivating account of ${this.userDisplayName()}...`;
    this.toastReset2FaAction?.show();

    const obs$ = this.activate()
      ? this.usersService.activateUser$(this.userId()!)
      : this.usersService.deactivateUser$(this.userId()!);

    obs$.subscribe({
      next: () => this.handleAfterExecute(true),
      error: () => this.handleAfterExecute(false)
    });
  }

  private handleAfterExecute(success: boolean): void {
    if (success) {
      this.processingMessage = `Account of ${this.userDisplayName()} ${this.activate() ? 'activated' : 'deactivated'} successfully.`;
      this.processComplete.emit();

      setTimeout(() => {
        this.resetToast();
      }, 1500);
    } else {
      this.resetToast();
    }

    this.processing = false;
    this.dialog?.hide();
  }

  private resetToast() {
    this.toastReset2FaAction?.hide();
    this.processingMessage = '';
  }
}
